// Translates the GET /provider/labour-management/report payload into the display
// shapes the table renders. Everything here is defensive: the endpoint's enums
// (type / source / status / audit action) are open-ended, so unknown values fall
// back to a title-cased label rather than rendering blank.

import moment from 'moment';
import 'moment-timezone';
import { getInitials } from 'src/utils/get-initials';
import type {
  AuditEvent,
  AuditKind,
  EntrySource,
  EntryStatus,
  EntryType,
  LaborEntry,
  PageInfo,
  PayPeriod,
  TherapistApproval,
} from './labor-report-data';

const EM_DASH = '—';

/** INCORRECT_TIME_OUT -> "Incorrect Time Out" */
export const humanizeEnum = (value?: string | null): string =>
  (value || '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const TYPE_ICONS: Record<string, EntryType> = {
  BUILDING: { label: 'Building', icon: 'building' },
  IN_BUILDING: { label: 'Building', icon: 'building' },
  IN_CLINIC: { label: 'In Clinic', icon: 'building' },
  DRIVE: { label: 'Drive', icon: 'drive' },
  MILEAGE: { label: 'Mileage', icon: 'drive' },
  IN_HOME: { label: 'In Home', icon: 'home' },
  HOME: { label: 'In Home', icon: 'home' },
};

const mapType = (type?: string | null): EntryType =>
  TYPE_ICONS[(type || '').toUpperCase()] || {
    label: humanizeEnum(type) || EM_DASH,
    icon: 'other',
  };

const STATUSES: Record<string, EntryStatus> = {
  APPROVED: 'approved',
  NOT_APPROVED: 'not-approved',
};

// Anything the API hasn't acted on yet (PENDING, NOT_REVIEWED, null, …) is "pending".
const mapStatus = (status?: string | null): EntryStatus =>
  STATUSES[(status || '').toUpperCase()] || 'pending';

const SOURCE_LABELS: Record<string, string> = {
  MANUAL: 'Self-entry',
  SELF: 'Self-entry',
  SELF_ENTRY: 'Self-entry',
  EDITED: 'Edited',
  SYSTEM: 'System',
  CLOCK: 'Clock',
};

const AUDIT_KINDS: Record<string, AuditKind> = {
  CREATED: 'entered',
  EDITED: 'entered',
  APPROVED: 'approved',
  NOT_APPROVED: 'not-approved',
  UNAPPROVED: 'entered',
};

export const mapAuditEvent = (event: any): AuditEvent => ({
  kind: AUDIT_KINDS[(event?.action || '').toUpperCase()] || 'entered',
  // `summary` is the human sentence ("Entry created", "Clock Out: 1:20 PM -> 12:55 PM");
  // `performedBy` is who did it.
  text: [event?.summary || humanizeEnum(event?.action), event?.performedBy]
    .filter(Boolean)
    .join(' · '),
  // Already formatted server-side, e.g. "08/18/2026 09:00 AM EDT".
  time: event?.timestamp || '',
  reason: event?.reason ? humanizeEnum(event.reason) : undefined,
});

/**
 * The per-entry history endpoint (`/labour-management/{id}/history`) returns the same
 * event shape the report embeds, but as the payload's whole body. Accept the plain
 * array as well as the shapes that wrap it.
 */
export const mapAuditHistory = (data: any): AuditEvent[] => {
  const events = Array.isArray(data)
    ? data
    : Array.isArray(data?.auditHistory)
    ? data.auditHistory
    : Array.isArray(data?.history)
    ? data.history
    : Array.isArray(data?.content)
    ? data.content
    : [];
  return events.map(mapAuditEvent);
};

/**
 * Clock punches come back as UTC instants ("2026-08-18T13:00:00Z") and must be read
 * in the facility's zone — the same zoneId sent as a query param.
 */
const formatClock = (iso?: string | null, zoneId?: string): string => {
  if (!iso) return EM_DASH;
  const stamp = zoneId ? moment(iso).tz(zoneId) : moment(iso);
  return stamp.isValid() ? stamp.format('h:mm A') : EM_DASH;
};

/** Timestamps that may arrive either as an ISO instant or pre-formatted by the API. */
const formatStamp = (value?: string | null, zoneId?: string): string => {
  if (!value) return '';
  const stamp = moment(value, moment.ISO_8601, true);
  if (!stamp.isValid()) return value;
  return (zoneId ? stamp.tz(zoneId) : stamp).format('MMM D, YYYY, h:mm A');
};

const formatTotal = (hr?: number | null, min?: number | null): string =>
  `${String(hr ?? 0).padStart(2, '0')}:${String(min ?? 0).padStart(2, '0')}`;

const statusNoteFor = (entry: any, status: EntryStatus, zoneId?: string): string | undefined => {
  const history: any[] = Array.isArray(entry?.auditHistory) ? entry.auditHistory : [];
  const lastAction = [...history]
    .reverse()
    .find((event) => (event?.action || '').toUpperCase() === (entry?.status || '').toUpperCase());

  if (status === 'approved') {
    const by = entry?.approvedBy || lastAction?.performedBy;
    const at = entry?.approvedAt ? formatStamp(entry.approvedAt, zoneId) : lastAction?.timestamp;
    if (!by && !at) return undefined;
    return `Approved${by ? ` by ${by}` : ''}${at ? ` · ${at}` : ''}`;
  }

  if (status === 'deleted') {
    const by = entry?.deletedBy;
    const at = entry?.deletedAt ? formatStamp(entry.deletedAt, zoneId) : '';
    if (!by && !at) return undefined;
    return `Deleted${by ? ` by ${by}` : ''}${at ? ` · ${at}` : ''}`;
  }

  if (status === 'not-approved') {
    const by = lastAction?.performedBy;
    const at = lastAction?.timestamp;
    const reason = humanizeEnum(entry?.notApprovedReason);
    const who = `Not approved${by ? ` by ${by}` : ''}${at ? ` · ${at}` : ''}`;
    return reason ? `${who}${by || at ? ' · ' : ''}${reason}` : who;
  }

  return undefined;
};

const mapEntry = (entry: any, zoneId?: string): LaborEntry => {
  // A deleted punch is no longer actionable — it reads as Deleted, not Pending.
  const status: EntryStatus = entry?.deleted ? 'deleted' : mapStatus(entry?.status);
  const history: any[] = Array.isArray(entry?.auditHistory) ? entry.auditHistory : [];
  const rawSource = (entry?.source || '').toUpperCase();
  // The "Edited" badge means the punch was changed after it was entered — either the
  // API says so outright or the audit trail carries an EDITED action.
  const edited =
    rawSource === 'EDITED' ||
    history.some((event) => (event?.action || '').toUpperCase() === 'EDITED');
  // "Maria Alvarez" + "PT" -> "Maria Alvarez, PT"
  const therapistName = [entry?.therapistName, entry?.discipline].filter(Boolean).join(', ');
  const day = moment(entry?.date, 'YYYY-MM-DD', true);

  const source: EntrySource = {
    label: edited ? 'Edited' : SOURCE_LABELS[rawSource] || humanizeEnum(entry?.source) || EM_DASH,
    edited,
  };

  return {
    id: entry?.labourManagementId,
    date: day.isValid() ? day.format('ddd, MMM D') : entry?.date || EM_DASH,
    sortDate: entry?.date || '',
    facilityName: entry?.facilityName || EM_DASH,
    therapist: {
      name: therapistName || EM_DASH,
      initials: getInitials(entry?.therapistName || ''),
    },
    type: mapType(entry?.type),
    // Already display-ready server-side ("REG", "PTO", "Rehab Trav"), so it is shown as sent
    // rather than title-cased — humanizing would turn "REG" into "Reg".
    laborCode: entry?.laborCode || EM_DASH,
    clockIn: formatClock(entry?.clockIn, zoneId),
    clockOut: formatClock(entry?.clockOut, zoneId),
    totalHours: formatTotal(entry?.totalHr, entry?.totalMin),
    source,
    status,
    deleted: !!entry?.deleted,
    statusNote: statusNoteFor(entry, status, zoneId),
    auditHistory: history.map(mapAuditEvent),
  };
};

const formatRange = (start: moment.Moment, end: moment.Moment): string =>
  start.year() === end.year()
    ? `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`
    : `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;

const resolveBounds = (data: any, entries: LaborEntry[], length: number, known?: string) => {
  const echoed = known || data?.periodStartDate || data?.periodStart || data?.startDate;
  const start = moment(echoed, 'YYYY-MM-DD', true);
  if (start.isValid()) {
    return { start, end: start.clone().add(Math.max(length, 1) - 1, 'days') };
  }

  const days = entries
    .map((entry) => entry.sortDate)
    .filter(Boolean)
    .sort();
  if (days.length) {
    return {
      start: moment(days[0], 'YYYY-MM-DD'),
      end: moment(days[days.length - 1], 'YYYY-MM-DD'),
    };
  }

  return { start: null, end: null };
};

const mapTherapistApproval = (raw: any): TherapistApproval | undefined => {
  if (!raw) return undefined;
  const totalCount = Number(raw?.totalCount) || 0;
  const approvedCount = Number(raw?.approvedCount) || 0;
  return {
    totalCount,
    approvedCount,
    pendingCount: Number(raw?.pendingCount) || 0,
    notApprovedCount: Number(raw?.notApprovedCount) || 0,
    fullyApproved: raw?.fullyApproved ?? (totalCount > 0 && approvedCount === totalCount),
  };
};

const mapPageInfo = (page: any, loadedCount: number): PageInfo => {
  const pageSize = Number(page?.size ?? page?.pageable?.pageSize) || loadedCount || 0;
  const totalElements = Number(page?.totalElements ?? loadedCount) || 0;
  const totalPages =
    Number(page?.totalPages) ||
    (totalElements && pageSize ? Math.ceil(totalElements / pageSize) : 1);
  const number = Number(page?.number ?? page?.pageable?.pageNumber) || 0;
  return {
    page: number,
    pageSize,
    totalElements,
    totalPages: Math.max(totalPages, 1),
    first: page?.first ?? number === 0,
    last: page?.last ?? number >= Math.max(totalPages, 1) - 1,
  };
};

const cadenceFor = (
  length: number,
  start?: moment.Moment | null,
  end?: moment.Moment | null
): { cadence: string; weekdayRange?: string } => {
  // The weekday range describes the cycle's own boundaries, not a hardcoded
  // "Sunday through Saturday" — a period starting on a Monday says Monday through Sunday.
  // Returned separately from `cadence` so the UI can style it distinctly.
  const weekdayRange =
    start?.isValid() && end?.isValid()
      ? `${start.format('dddd')} through ${end.format('dddd')}`
      : undefined;
  const cadence =
    length > 0 && length % 7 === 0
      ? `Fixed ${length / 7 === 1 ? 'one' : length / 7 === 2 ? 'two' : length / 7}-week pay period`
      : length > 0
      ? `Fixed ${length}-day pay period`
      : 'Pay period';
  return { cadence, weekdayRange };
};

export const mapLaborReport = (
  data: any,
  filterDate: string,
  zoneId?: string,
  override?: { startDate?: string; lengthDays?: number }
): PayPeriod => {
  // `entries` is a Spring page envelope; older payloads sent a bare array.
  const entriesPage = data?.entries;
  const rawEntries: any[] = Array.isArray(entriesPage)
    ? entriesPage
    : Array.isArray(entriesPage?.content)
    ? entriesPage.content
    : [];
  const entries = rawEntries
    .map((entry) => mapEntry(entry, zoneId))
    .sort((a, b) => a.sortDate.localeCompare(b.sortDate));
  const periodLengthDays = Number(override?.lengthDays) || Number(data?.periodLengthDays) || 14;
  const { start, end } = resolveBounds(data, entries, periodLengthDays, override?.startDate);
  const anchor = moment(filterDate, 'YYYY-MM-DD');

  const echoedStatus = (data?.periodStatus || data?.status || '').toUpperCase();
  const approver = data?.approvedBy;
  const { cadence, weekdayRange } = cadenceFor(periodLengthDays, start, end);

  return {
    label:
      start && end
        ? formatRange(start, end)
        : anchor.isValid()
        ? anchor.format('MMM D, YYYY')
        : filterDate,
    status: echoedStatus === 'APPROVED' ? 'APPROVED' : 'PARTIALLY_APPROVED',
    cadence,
    weekdayRange,
    validationDue: data?.validationDueAt ? formatStamp(data.validationDueAt, zoneId) : undefined,
    periodLengthDays,
    startDate: start ? start.format('YYYY-MM-DD') : undefined,
    endDate: end ? end.format('YYYY-MM-DD') : undefined,
    hasWriteAccess: !!data?.hasWriteAccess,
    showApprovedPayPeriod: !!data?.showApprovedPayPeriod,
    remainingEntriesMessage: data?.remainingEntriesMessage || undefined,
    therapistApproval: mapTherapistApproval(data?.therapistApprovalStatus),
    pagination: mapPageInfo(entriesPage, entries.length),
    approvedBy: approver
      ? {
          name: typeof approver === 'string' ? approver : approver?.name || '',
          role: typeof approver === 'string' ? '' : approver?.role || '',
          at: formatStamp(data?.approvedAt, zoneId),
        }
      : undefined,
    entries,
  };
};
